/**
 * @fileoverview useWebAuthnStepUp – React hook for high-assurance step-up
 * verification on sensitive actions.
 *
 * Uses the existing in-session WebAuthn authentication flow
 * (`webauthn-authenticate` + `webauthn-authenticate-verify`) to re-verify the
 * user's identity before performing privileged operations such as rotating
 * division encryption keys or decrypting sensitive submission data.
 *
 * The result of a successful verification is recorded in `sessionStorage` so
 * that subsequent step-up checks within the same browser session do not
 * immediately re-prompt the user (grace window: 5 minutes).
 *
 * @module hooks/useWebAuthnStepUp
 */

import { useState, useCallback } from 'react';
import { createLogger } from '@xanectech/lib';
import { authenticateWithWebAuthn } from '../services/WebAuthnService';
import { useAuth } from '../contexts/AuthContext';
import { isWebAuthnSupported } from '../services/WebAuthnService';

const logger = createLogger('useWebAuthnStepUp');

/** Grace period after a successful step-up (ms).  Within this window a
 *  repeated step-up check will not re-prompt the user. */
const STEP_UP_GRACE_PERIOD_MS = 5 * 60 * 1000; // 5 minutes

const STORAGE_KEY = 'xanec:webauthn-step-up-verified-at';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getLastVerifiedAt(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const ts = parseInt(raw, 10);
    return isNaN(ts) ? null : ts;
  } catch {
    return null;
  }
}

function setLastVerifiedAt(ts: number): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(ts));
  } catch {
    // sessionStorage may be unavailable in some environments – non-fatal
  }
}

function clearLastVerifiedAt(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // non-fatal
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface StepUpResult {
  success: boolean;
  error?: string;
}

export function useWebAuthnStepUp() {
  const { user } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Returns true if the user has verified with WebAuthn within the grace
   * period and does not need to be prompted again.
   */
  const hasRecentVerification = useCallback((): boolean => {
    const lastVerifiedAt = getLastVerifiedAt();
    if (lastVerifiedAt === null) return false;
    return Date.now() - lastVerifiedAt < STEP_UP_GRACE_PERIOD_MS;
  }, []);

  /**
   * Trigger a WebAuthn step-up verification.
   *
   * If the user has verified within the grace period this returns
   * `{ success: true }` immediately without prompting the user.
   *
   * @param forcePrompt – If true, ignores the grace period and always prompts.
   */
  const verifyStepUp = useCallback(
    async (forcePrompt = false): Promise<StepUpResult> => {
      if (!user) {
        return {
          success: false,
          error: 'User must be authenticated to perform step-up verification',
        };
      }

      if (!isWebAuthnSupported()) {
        return {
          success: false,
          error:
            'Passkey authentication is not supported in this browser. Please use a different browser.',
        };
      }

      // Grace window bypass
      if (!forcePrompt && hasRecentVerification()) {
        logger.info('Step-up skipped: recent verification still valid');
        return { success: true };
      }

      setIsVerifying(true);
      setError(null);

      try {
        logger.info('Requesting WebAuthn step-up verification', {
          userId: user.id,
        });

        const result = await authenticateWithWebAuthn({ userId: user.id });

        if (!result.success) {
          const errorMsg =
            result.error ?? 'Step-up verification failed. Please try again.';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }

        // Record the successful verification timestamp
        setLastVerifiedAt(Date.now());

        logger.info('WebAuthn step-up verification succeeded', {
          credentialId: result.credentialId,
        });

        return { success: true };
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : 'Step-up verification failed. Please try again.';
        logger.error('Unexpected error during step-up verification', err as Error);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setIsVerifying(false);
      }
    },
    [user, hasRecentVerification],
  );

  /** Invalidate any cached step-up verification (e.g. on sign-out). */
  const invalidateStepUp = useCallback(() => {
    clearLastVerifiedAt();
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    verifyStepUp,
    hasRecentVerification,
    invalidateStepUp,
    isVerifying,
    error,
    clearError,
  };
}
